import { prefillSearchInputFromUrl } from './catalog-search-url.js';
import { closeFlyout, isFlyoutOpen, openFlyout } from './header-flyout.js';

const MQ_DESKTOP = '(min-width: 992px)';

function isDesktop() {
  return window.matchMedia(MQ_DESKTOP).matches;
}

function syncClearButton(wrap, input) {
  const clearBtn = wrap?.querySelector('[data-search-clear]');
  const form = wrap?.querySelector('.header-search');
  const filled = Boolean(input?.value.trim());
  if (clearBtn) {
    clearBtn.hidden = !filled;
  }
  form?.classList.toggle('is-filled', filled);
}

function setSearchLoading(wrap, loading) {
  const form = wrap?.querySelector('.header-search');
  const clearBtn = wrap?.querySelector('[data-search-clear]');
  form?.classList.toggle('is-loading', loading);
  if (clearBtn) {
    clearBtn.disabled = loading;
    clearBtn.setAttribute('aria-label', loading ? 'Загрузка' : 'Очистить');
  }
}

function shouldShowSearchLoader(control, input) {
  const value = input?.value ?? '';
  if (value.trim().length < 2 || !control) {
    return false;
  }
  const cacheKey = `${control.arParams.INPUT_ID}|${value}`;
  return control.cache?.[cacheKey] == null;
}

function renderSearchSkeleton(panel, count = 8) {
  if (!panel || panel.dataset.searchSkeleton === '1') {
    return;
  }

  const items = Array.from({ length: count }, () => (
    '<div class="header-search__item header-search__item--skeleton">' +
      '<span class="header-search__thumb header-search__thumb--skeleton"></span>' +
      '<span class="header-search__title header-search__title--skeleton"></span>' +
    '</div>'
  )).join('');

  panel.innerHTML = (
    '<div class="header-search__results header-search__results--skeleton">' +
      '<div class="header-search__results-inner">' +
        '<div class="header-search__list">' + items + '</div>' +
      '</div>' +
    '</div>'
  );
  panel.dataset.searchSkeleton = '1';
}

function clearSearchSkeleton(panel) {
  if (!panel || panel.dataset.searchSkeleton !== '1') {
    return;
  }
  panel.innerHTML = '';
  delete panel.dataset.searchSkeleton;
}

function hasSearchHits(panel, input) {
  const query = input?.value.trim() || '';
  return (
    query.length >= 2 &&
    Boolean(panel && panel.innerHTML.trim() !== '')
  );
}

export function initHeaderSearchPanel() {
  const panel = document.getElementById('header-search-panel');
  const input = document.getElementById('header-search-input');
  const wrap = document.getElementById('header-search');
  const catalogPanel = document.querySelector('[data-catalog-dropdown]');
  const catalogToggle = document.querySelector('[data-catalog-toggle]');

  if (!panel || panel.dataset.headerSearchPanelInited === '1') {
    return;
  }

  panel.dataset.headerSearchPanelInited = '1';

  const closeSearch = ({ instant = false } = {}) => {
    closeFlyout(panel, { instant });
  };

  const openSearch = () => {
    if (!isDesktop()) {
      return;
    }

    const fromMenu = isFlyoutOpen(catalogPanel);
    if (fromMenu) {
      document.dispatchEvent(new CustomEvent('pc-header-catalog-close', { detail: { instant: true } }));
    }
    openFlyout(panel, { instant: fromMenu });
  };

  const sync = () => {
    if (!isDesktop()) {
      return;
    }

    if (hasSearchHits(panel, input)) {
      if (!isFlyoutOpen(panel)) {
        openSearch();
      }
      return;
    }

    if (isFlyoutOpen(panel)) {
      closeSearch();
    }
  };

  wrap?.addEventListener('click', (event) => {
    event.stopPropagation();
  });

  panel.addEventListener('click', (event) => {
    event.stopPropagation();
  });

  document.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Node) || !isFlyoutOpen(panel)) {
      return;
    }
    if (panel.contains(target) || wrap?.contains(target) || catalogToggle?.contains(target)) {
      return;
    }
    closeSearch();
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && isFlyoutOpen(panel)) {
      closeSearch();
    }
  });

  document.addEventListener('pc-header-search-close', (event) => {
    closeSearch({ instant: Boolean(event.detail?.instant) });
  });

  prefillSearchInputFromUrl(input);
  syncClearButton(wrap, input);

  wrap?.querySelector('[data-search-clear]')?.addEventListener('click', (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (input) {
      input.value = '';
      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.focus();
    }
    syncClearButton(wrap, input);
    setSearchLoading(wrap, false);
    closeSearch({ instant: true });
  });

  input?.addEventListener('input', () => {
    const loading = shouldShowSearchLoader(window.jsControlHeader, input);
    syncClearButton(wrap, input);
    setSearchLoading(wrap, loading);
    if (loading) {
      renderSearchSkeleton(panel, 8);
    } else {
      clearSearchSkeleton(panel);
    }
    sync();
  });

  const bindSearchControl = () => {
    const control = window.jsControlHeader;
    if (!control || control.__headerSearchPanelBound) {
      return;
    }
    control.__headerSearchPanelBound = true;
    const originalShow = control.ShowResult.bind(control);
    control.ShowResult = function patchedShow(result) {
      setSearchLoading(wrap, false);
      clearSearchSkeleton(panel);
      originalShow(result);
      panel.style.removeProperty('display');
      sync();
    };
    const originalFinish = control.finishChange.bind(control);
    control.finishChange = function patchedFinish(callback) {
      if (!control.pendingChange) {
        setSearchLoading(wrap, false);
        if (!hasSearchHits(panel, input)) {
          clearSearchSkeleton(panel);
        }
      }
      originalFinish(callback);
    };
  };

  bindSearchControl();
  document.addEventListener('DOMContentLoaded', bindSearchControl);
  window.setTimeout(bindSearchControl, 0);
}
